import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DOMPurify from 'dompurify';

import ClearTeamAssistAnswer from 'actions/team_assist/clear_team_assist_answer';
import ExecuteTeamAssistProposedAction from 'actions/team_assist/execute_team_assist_proposed_action';
import FetchTeamAssistAnswer from 'actions/team_assist/fetch_team_assist_answer';
import RateTeamAssistMessage from 'actions/team_assist/rate_team_assist_message';
import RejectTeamAssistProposedAction from 'actions/team_assist/reject_team_assist_proposed_action';
import RetryAskTeamAssist from 'actions/team_assist/retry_ask_team_assist';
import RetryTeamAssist from 'actions/team_assist/retry_team_assist';
import UseTeamAssistResponse from 'actions/team_assist/use_team_assist_response';
import Button, { ButtonTypes } from 'components/common/button';
import ThumbsDownIcon from 'components/common/icons/stroke/thumbs-down-stroke';
import ThumbsUpIcon from 'components/common/icons/stroke/thumbs-up-stroke';
import ModalCard from 'components/common/modal_card';
import SafeHtml from 'components/common/safe_html';
import Tooltip from 'components/common/tooltip';
import TeamAssistAnswerPreviewModal from 'components/customer/team_assist/team_assist_answer_preview_modal';
import {
  formatProposedActionArguments,
  formatToolDisplayName,
} from 'components/customer/team_assist/team_assist_message_helpers';
import { TeamAssistLoadingDots } from 'components/customer/team_assist/team_assist_shared';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import PortalModalWrapper from 'components/lib/portal_modal_wrapper';
import TeamAssistMessage from 'models/team_assist_message';

function MessageBubble({ execution, isLastMessage, msg, onUse }) {
  const executeAction = useExecuteAction();
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [previewAnswerId, setPreviewAnswerId] = useState(null);
  const [isUseClicked, setIsUseClicked] = useState(false);
  // Synchronous latch for the Execute button. `msg.isExecuting` only
  // reflects the prior render's store snapshot, so between the first
  // click's dispatch and the next render, a rapid second click would see
  // `isExecuting === false` and double-dispatch — unacceptable for
  // destructive tools. The ref flips synchronously inside the click
  // handler and clears once the store catches up to `isExecuting === false`.
  const isExecutingRef = useRef(false);
  useEffect(() => {
    if (!msg.isExecuting) {
      isExecutingRef.current = false;
    }
  }, [msg.isExecuting]);
  const hasCitations = msg.citations && msg.citations.length > 0;
  const displayContent = formatMessageContent(
    hasCitations ? replaceCitationRefsInContent(msg.content, msg.citations) : msg.content
  );

  const handleCopy = (content, messageId) => {
    const plainText = getPlainTextFromHtml(content);
    const html = content || '';
    if (!plainText && !html.trim()) return;

    const writeClipboard = () => {
      const item = {
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      };
      return navigator.clipboard.write([new window.ClipboardItem(item)]);
    };

    writeClipboard()
      .then(() => {
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      })
      .catch(() => {
        navigator.clipboard.writeText(plainText).then(() => {
          setCopiedMessageId(messageId);
          setTimeout(() => setCopiedMessageId(null), 2000);
        });
      });
  };

  if (msg.role === TeamAssistMessage.Role.agent_question) {
    return (
      <UserRow data-aid="teamAssistUserMessage">
        <UserBubbleSpacer />
        <UserMessageContent>
          <UserBubble hasError={msg.error}>
            <SafeHtml html={formatMessageContent(msg.displayedMessage || msg.content)} />
          </UserBubble>
          {msg.error && (
            <ErrorRow data-aid="teamAssistMessageError">
              <ErrorIcon className="fa fa-exclamation-circle" />
              <ErrorText>Failed to send</ErrorText>
              {isLastMessage && (
                <ErrorRetryButton
                  aria-label="Retry sending message"
                  data-aid="teamAssistRetryAskButton"
                  onClick={() =>
                    executeAction(RetryAskTeamAssist, {
                      customerId: msg.customerId,
                      messageId: msg.id,
                    })
                  }
                  type="button"
                >
                  <i aria-hidden="true" className="fa fa-repeat" /> Retry
                </ErrorRetryButton>
              )}
            </ErrorRow>
          )}
        </UserMessageContent>
      </UserRow>
    );
  }

  const isCopied = copiedMessageId === msg.id;
  const copyButton = (
    <Tooltip delay={250} message={isCopied ? 'Copied' : 'Copy'} position="bottom" style={{ padding: '5px 10px' }}>
      <CopyButton
        aria-label={isCopied ? 'Copied response' : 'Copy response'}
        data-aid="teamAssistCopyMessageButton"
        isCopied={isCopied}
        onClick={() => handleCopy(displayContent, msg.id)}
        type="button"
      >
        <i aria-hidden="true" className={isCopied ? 'fa fa-check' : 'fa fa-copy'} />
      </CopyButton>
    </Tooltip>
  );

  if (msg.type === TeamAssistMessage.Type.proposed_action) {
    return (
      <AssistantRow data-aid="teamAssistProposedAction">
        <AssistantMessageWrapper>
          <AssistantContent>
            <ProposedActionCard
              execution={execution}
              isExecuting={Boolean(msg.isExecuting) && !execution}
              isRejected={Boolean(msg.isRejected) && !execution}
              msg={msg}
              onExecute={() => {
                // Check the synchronous latch FIRST so a rapid double-
                // click within a single render frame can't dispatch
                // twice — `msg.isExecuting` only catches a click that
                // happens after the first dispatch's re-render landed.
                if (isExecutingRef.current || msg.isExecuting || execution || msg.isRejected) {
                  return;
                }
                isExecutingRef.current = true;
                executeAction(ExecuteTeamAssistProposedAction, {
                  customerId: msg.customerId,
                  messageId: msg.id,
                  toolName: msg.proposedAction && msg.proposedAction.toolName,
                  arguments: (msg.proposedAction && msg.proposedAction.arguments) || {},
                });
              }}
              onReject={() => {
                if (msg.isExecuting || execution || msg.isRejected) return;
                executeAction(RejectTeamAssistProposedAction, {
                  customerId: msg.customerId,
                  messageId: msg.id,
                });
              }}
            />
          </AssistantContent>
        </AssistantMessageWrapper>
      </AssistantRow>
    );
  }

  if (msg.type === TeamAssistMessage.Type.suggested_response) {
    return (
      <AssistantRow data-aid="teamAssistAssistantMessage">
        <AssistantMessageWrapper>
          <AssistantContent>
            {msg.isRetrying ? (
              <TeamAssistLoadingDots />
            ) : (
              <>
                <SuggestedResponseContent>
                  <SafeHtml html={displayContent || ''} />
                </SuggestedResponseContent>
                <ReviewReasons reviewReasons={msg.reviewReasons} />
                <SuggestedResponseActions>
                  <UseButton
                    aria-label="Use suggested response"
                    buttonType={ButtonTypes.PRIMARY}
                    data-aid="teamAssistUseButton"
                    disabled={isUseClicked}
                    isSmall
                    onClick={() => {
                      setIsUseClicked(true);
                      executeAction(UseTeamAssistResponse, {
                        customerId: msg.customerId,
                        messageId: msg.id,
                      });
                      onUse?.();
                    }}
                    type="button"
                  >
                    Use
                  </UseButton>
                  <Tooltip delay={250} message="Retry" position="bottom" style={{ padding: '5px 10px' }}>
                    <RetryIconButton
                      aria-label="Retry suggested response"
                      data-aid="teamAssistRetryButton"
                      onClick={() =>
                        executeAction(RetryTeamAssist, {
                          customerId: msg.customerId,
                          messageId: msg.id,
                        })
                      }
                      type="button"
                    >
                      <i aria-hidden="true" className="fa fa-repeat" />
                    </RetryIconButton>
                  </Tooltip>
                  <SuggestedResponseCopy>{copyButton}</SuggestedResponseCopy>
                  <FeedbackButtons msg={msg} />
                </SuggestedResponseActions>
                <Citations
                  citations={msg.citations}
                  onCitationClick={answerId => {
                    setPreviewAnswerId(answerId);
                    executeAction(FetchTeamAssistAnswer, { answerId });
                  }}
                  onClosePreview={() => {
                    setPreviewAnswerId(null);
                    executeAction(ClearTeamAssistAnswer);
                  }}
                  previewAnswerId={previewAnswerId}
                />
              </>
            )}
          </AssistantContent>
        </AssistantMessageWrapper>
      </AssistantRow>
    );
  }

  return (
    <AssistantRow data-aid="teamAssistAssistantMessage">
      <AssistantMessageWrapper>
        <AssistantContent>
          <SuggestedResponseContent>
            <SafeHtml html={displayContent || ''} />
          </SuggestedResponseContent>
          <ReviewReasons reviewReasons={msg.reviewReasons} />
          <Citations
            citations={msg.citations}
            onCitationClick={answerId => {
              setPreviewAnswerId(answerId);
              executeAction(FetchTeamAssistAnswer, { answerId });
            }}
            onClosePreview={() => {
              setPreviewAnswerId(null);
              executeAction(ClearTeamAssistAnswer);
            }}
            previewAnswerId={previewAnswerId}
          />
          <AssistantActions>
            {copyButton}
            <FeedbackButtons msg={msg} />
          </AssistantActions>
        </AssistantContent>
      </AssistantMessageWrapper>
    </AssistantRow>
  );
}

MessageBubble.propTypes = {
  execution: PropTypes.shape({
    isError: PropTypes.bool,
    result: PropTypes.string,
  }),
  isLastMessage: PropTypes.bool,
  msg: PropTypes.shape({
    citations: PropTypes.arrayOf(
      PropTypes.shape({
        snippetId: PropTypes.string,
        snippetName: PropTypes.string,
      })
    ),
    confidence: PropTypes.number,
    content: PropTypes.string,
    customerId: PropTypes.string,
    displayedMessage: PropTypes.string,
    error: PropTypes.bool,
    id: PropTypes.string,
    isExecuting: PropTypes.bool,
    isRejected: PropTypes.bool,
    isRetrying: PropTypes.bool,
    proposedAction: PropTypes.shape({
      toolName: PropTypes.string,
      summary: PropTypes.string,
      arguments: PropTypes.any,
    }),
    reviewReasons: PropTypes.arrayOf(PropTypes.string),
    rating: PropTypes.object,
    role: PropTypes.string,
    type: PropTypes.string,
  }),
  onUse: PropTypes.func,
};

export default MessageBubble;
export { AssistantRow };

function FeedbackButtons({ msg }) {
  const executeAction = useExecuteAction();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const ratingValue = msg.rating ? msg.rating.value : null;
  const isThumbsUp = ratingValue === TeamAssistMessage.RatingValue.thumbs_up;
  const isThumbsDown = ratingValue === TeamAssistMessage.RatingValue.thumbs_down;

  const handleThumbsUp = () => {
    const rating = isThumbsUp ? null : { value: TeamAssistMessage.RatingValue.thumbs_up };
    executeAction(RateTeamAssistMessage, {
      customerId: msg.customerId,
      messageId: msg.id,
      rating,
    });
  };

  const handleThumbsDown = () => {
    if (isThumbsDown) {
      executeAction(RateTeamAssistMessage, {
        customerId: msg.customerId,
        messageId: msg.id,
        rating: null,
      });
    } else {
      setShowFeedbackModal(true);
    }
  };

  const handleFeedbackSubmit = ({ reason, comment }) => {
    const rating = { value: TeamAssistMessage.RatingValue.thumbs_down };
    if (reason) rating.reason = reason;
    if (comment) rating.comment = comment;
    executeAction(RateTeamAssistMessage, {
      customerId: msg.customerId,
      messageId: msg.id,
      rating,
    });
    setShowFeedbackModal(false);
  };

  return (
    <>
      <Tooltip delay={250} message="Good response" position="bottom" style={{ padding: '5px 10px' }}>
        <ThumbButton
          aria-label={isThumbsUp ? 'Remove thumbs up' : 'Thumbs up'}
          data-aid="teamAssistThumbsUpButton"
          isActive={isThumbsUp}
          onClick={handleThumbsUp}
          type="button"
        >
          <ThumbsUp />
        </ThumbButton>
      </Tooltip>
      <Tooltip delay={250} message="Bad response" position="bottom" style={{ padding: '5px 10px' }}>
        <ThumbButton
          aria-label={isThumbsDown ? 'Remove thumbs down' : 'Thumbs down'}
          data-aid="teamAssistThumbsDownButton"
          isActive={isThumbsDown}
          onClick={handleThumbsDown}
          type="button"
        >
          <ThumbsDown />
        </ThumbButton>
      </Tooltip>
      {showFeedbackModal && (
        <TeamAssistFeedbackModal onClose={() => setShowFeedbackModal(false)} onSubmit={handleFeedbackSubmit} />
      )}
    </>
  );
}

FeedbackButtons.propTypes = {
  msg: PropTypes.shape({
    customerId: PropTypes.string,
    id: PropTypes.string,
    rating: PropTypes.object,
  }),
};

const FEEDBACK_REASONS = [
  { value: TeamAssistMessage.RatingReason.incorrect_or_incomplete, label: 'Incorrect or incomplete' },
  { value: TeamAssistMessage.RatingReason.slow_or_buggy, label: 'Slow or buggy' },
  { value: TeamAssistMessage.RatingReason.style_or_tone, label: 'Style or tone' },
  { value: TeamAssistMessage.RatingReason.other, label: 'Other' },
];

function TeamAssistFeedbackModal({ onClose, onSubmit }) {
  const [selectedReason, setSelectedReason] = useState(null);
  const [comment, setComment] = useState('');

  return (
    <PortalModalWrapper>
      <FeedbackModalOverlay>
        <ModalCard data-aid="teamAssistFeedbackModal" onClose={onClose}>
          <FeedbackModalContent>
            <FeedbackModalTitle>What went wrong?</FeedbackModalTitle>
            <FeedbackReasonButtons>
              {FEEDBACK_REASONS.map(({ value, label }) => (
                <FeedbackReasonButton
                  data-aid="teamAssistFeedbackReasonButton"
                  isSelected={selectedReason === value}
                  key={value}
                  onClick={() => setSelectedReason(selectedReason === value ? null : value)}
                  type="button"
                >
                  {label}
                </FeedbackReasonButton>
              ))}
            </FeedbackReasonButtons>
            <FeedbackCommentArea
              data-aid="teamAssistFeedbackComment"
              onChange={e => setComment(e.target.value)}
              placeholder="Share details (optional)"
              rows={3}
              value={comment}
            />
            <FeedbackModalActions>
              <Button buttonType={ButtonTypes.TEXT} data-aid="teamAssistFeedbackCancel" onClick={onClose}>
                Cancel
              </Button>
              <Button
                buttonType={ButtonTypes.PRIMARY}
                data-aid="teamAssistFeedbackSubmit"
                isSmall
                onClick={() => handleSubmit({ comment, onSubmit, selectedReason })}
              >
                Submit
              </Button>
            </FeedbackModalActions>
          </FeedbackModalContent>
        </ModalCard>
      </FeedbackModalOverlay>
    </PortalModalWrapper>
  );
}

function handleSubmit({ comment, onSubmit, selectedReason }) {
  onSubmit({ reason: selectedReason, comment: comment.trim() || null });
}

TeamAssistFeedbackModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

function ReviewReasons({ reviewReasons }) {
  if (!reviewReasons || reviewReasons.length === 0) {
    return null;
  }

  return (
    <ReviewReasonsContainer>
      <ReviewReasonsIcon className="fa fa-exclamation-triangle" />
      <ReviewReasonsText>
        <ReviewReasonsLabel>Needs review</ReviewReasonsLabel>
        {reviewReasons.map((reason, idx) => (
          <ReviewReason key={idx}>{reason}</ReviewReason>
        ))}
      </ReviewReasonsText>
    </ReviewReasonsContainer>
  );
}

ReviewReasons.propTypes = {
  reviewReasons: PropTypes.arrayOf(PropTypes.string),
};

function ProposedActionCard({ execution, isExecuting, isRejected, msg, onExecute, onReject }) {
  const [argsExpanded, setArgsExpanded] = useState(false);
  const [errorDetailsExpanded, setErrorDetailsExpanded] = useState(false);
  const proposedAction = msg.proposedAction || {};
  const toolName = proposedAction.toolName || '';
  const displayName = formatToolDisplayName(toolName);
  const argsPreview = formatProposedActionArguments(proposedAction.arguments);
  const isExecuted = Boolean(execution);
  const showButtons = !isExecuted && !isRejected;

  return (
    <ProposedActionContainer data-aid="teamAssistProposedActionCard">
      <ProposedActionHeader>
        <ProposedActionIcon className="fa fa-exclamation-triangle" />
        <ProposedActionTitleWrap>
          <ProposedActionLabel>Action awaiting review</ProposedActionLabel>
          {displayName && <ProposedActionToolName>{displayName}</ProposedActionToolName>}
        </ProposedActionTitleWrap>
      </ProposedActionHeader>
      {argsPreview && (
        <ProposedActionArgs>
          <ProposedActionExpander
            aria-expanded={argsExpanded}
            data-aid="teamAssistProposedActionArgsToggle"
            onClick={() => setArgsExpanded(v => !v)}
            type="button"
          >
            <i aria-hidden="true" className={`fa fa-caret-${argsExpanded ? 'down' : 'right'}`} />{' '}
            {argsExpanded ? 'Hide arguments' : 'Show arguments'}
          </ProposedActionExpander>
          {argsExpanded && <ProposedActionArgsBlock>{argsPreview}</ProposedActionArgsBlock>}
        </ProposedActionArgs>
      )}
      {showButtons && (
        <ProposedActionActions>
          <Button
            aria-label="Execute proposed action"
            buttonType={ButtonTypes.PRIMARY}
            data-aid="teamAssistProposedActionExecuteButton"
            disabled={isExecuting}
            isSmall
            onClick={onExecute}
          >
            {isExecuting ? 'Executing…' : 'Execute'}
          </Button>
          <Button
            aria-label="Reject proposed action"
            buttonType={ButtonTypes.TEXT}
            data-aid="teamAssistProposedActionRejectButton"
            disabled={isExecuting}
            isSmall
            onClick={onReject}
          >
            Reject
          </Button>
        </ProposedActionActions>
      )}
      {isRejected && !isExecuted && (
        <ProposedActionMutedState data-aid="teamAssistProposedActionRejected">Rejected</ProposedActionMutedState>
      )}
      {isExecuted && !execution.isError && (
        <ProposedActionMutedState data-aid="teamAssistProposedActionExecuted">Executed</ProposedActionMutedState>
      )}
      {isExecuted && execution.isError && (
        <ProposedActionErrorBlock data-aid="teamAssistProposedActionError">
          <ProposedActionErrorHeader>
            <ProposedActionErrorIcon className="fa fa-exclamation-circle" />
            <ProposedActionErrorText>Action failed</ProposedActionErrorText>
            {execution.result && (
              <ProposedActionErrorExpander
                aria-expanded={errorDetailsExpanded}
                data-aid="teamAssistProposedActionErrorToggle"
                onClick={() => setErrorDetailsExpanded(v => !v)}
                type="button"
              >
                {errorDetailsExpanded ? 'Hide error details' : 'Show error details'}
              </ProposedActionErrorExpander>
            )}
          </ProposedActionErrorHeader>
          {errorDetailsExpanded && execution.result && (
            <ProposedActionErrorBody data-aid="teamAssistProposedActionErrorBody">
              {execution.result}
            </ProposedActionErrorBody>
          )}
        </ProposedActionErrorBlock>
      )}
    </ProposedActionContainer>
  );
}

ProposedActionCard.propTypes = {
  execution: PropTypes.shape({
    isError: PropTypes.bool,
    result: PropTypes.string,
  }),
  isExecuting: PropTypes.bool,
  isRejected: PropTypes.bool,
  msg: PropTypes.shape({
    customerId: PropTypes.string,
    id: PropTypes.string,
    proposedAction: PropTypes.shape({
      toolName: PropTypes.string,
      summary: PropTypes.string,
      arguments: PropTypes.any,
    }),
  }),
  onExecute: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

function Citations({ citations, onCitationClick, onClosePreview, previewAnswerId }) {
  if (!citations || citations.length === 0) {
    return null;
  }

  return (
    <>
      <CitationsDivider />
      <CitationsLabel>Answers referenced</CitationsLabel>
      <CitationsList>
        {citations.map((citation, idx) => (
          <CitationLink
            aria-label={`Open referenced answer ${citation.snippetName || citation.snippetId}`}
            href="#"
            key={citation.snippetId}
            onClick={evt => {
              evt.preventDefault();
              onCitationClick(citation.snippetId);
            }}
          >
            <CitationNumber>[{idx + 1}]</CitationNumber> {citation.snippetName || citation.snippetId}
          </CitationLink>
        ))}
      </CitationsList>
      {previewAnswerId && <TeamAssistAnswerPreviewModal onClose={onClosePreview} />}
    </>
  );
}

Citations.propTypes = {
  citations: PropTypes.arrayOf(
    PropTypes.shape({
      snippetId: PropTypes.string,
      snippetName: PropTypes.string,
    })
  ),
  onCitationClick: PropTypes.func.isRequired,
  onClosePreview: PropTypes.func.isRequired,
  previewAnswerId: PropTypes.string,
};

function getPlainTextFromHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = DOMPurify.sanitize(html);
  return div.textContent || div.innerText || '';
}

function formatMessageContent(content) {
  if (!content) {
    return '';
  }
  if (containsHtml(content)) {
    return content;
  }
  return escapeHtml(content).replace(/\r\n|\r|\n/g, '<br />');
}

// Note: may false-positive on plain text with angle brackets (e.g. "x < y and z > 0"),
// but this is unlikely in practice since message content is either plain text or HTML from the backend.
function containsHtml(content) {
  return /<\/?[a-z][\s\S]*>/i.test(content);
}

function escapeHtml(content) {
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function replaceCitationRefsInContent(html, citations) {
  if (!html || !citations || citations.length === 0) return html;

  let result = html;
  citations.forEach((citation, idx) => {
    const num = idx + 1;
    const escapedId = citation.snippetId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const sup = `<sup class="cite-ref">[${num}]</sup>`;
    // Match patterns like (snippetId – Name) or (snippetId - Name), optionally wrapped in <em>/<i> tags
    const pattern = new RegExp(`(?:<(?:em|i)>)?\\(?${escapedId}(?:\\s*[–—-]\\s*[^)<]*)?\\)?(?:</(?:em|i)>)?`, 'g');
    result = result.replace(pattern, sup);
  });
  return result;
}

const UserBubbleSpacer = styled.div`
  flex-shrink: 0;
  width: 22px;
`;

const UserRow = styled.div`
  align-self: flex-end;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
  max-width: 100%;
  width: 100%;
`;

const AssistantRow = styled.div`
  align-self: flex-start;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
  max-width: 100%;
  width: 100%;
`;

const AssistantMessageWrapper = styled.div`
  align-items: stretch;
  display: flex;
  max-width: 100%;
  width: 100%;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  flex-shrink: 0;
  font-size: var(--font-size-medium);
  height: 16px;
  line-height: 1;
  margin-top: 2px;
  padding: 0;
  width: 16px;

  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

const UserBubble = styled.div`
  background: ${p => (p.hasError ? p.theme.colors.red100 : p.theme.colors.gray100)};
  border-radius: 12px 2px 12px 12px;
  color: ${p => p.theme.colors.black};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  max-width: 100%;
  overflow-wrap: break-word;
  padding: 8px 12px;
  word-break: break-word;
`;

const ErrorRow = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  margin-top: 4px;
`;

const ErrorIcon = styled.i`
  color: ${p => p.theme.colors.red400};
  font-size: var(--font-size-reduced);
`;

const ErrorText = styled.span`
  color: ${p => p.theme.colors.red400};
  font-size: var(--font-size-reduced);
`;

const ErrorRetryButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.red400};
  cursor: pointer;
  font-size: var(--font-size-reduced);
  padding: 0 0 0 4px;

  &:hover {
    color: ${p => p.theme.colors.red300};
    text-decoration: underline;
  }
`;

const AssistantContent = styled.div`
  color: ${p => p.theme.colors.black};
  flex: 1;
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  min-width: 0;
  word-wrap: break-word;

  .teamAssistImageVerificationTable {
    border-collapse: collapse;
    margin: 8px 0;

    tr:hover {
      background-color: ${p => p.theme.colors.gray100};
    }
    th,
    td {
      padding: 4px;
    }
    th {
      padding-right: 8px;
      text-align: right;
      vertical-align: top;
    }
  }

  .teamAssistImageVerificationRecommendation {
    border: 1px solid ${p => p.theme.colors.gray300};
    border-radius: 8px;
    margin: 8px 0;
    padding: 8px;
  }
`;

const AssistantActions = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  margin-top: 8px;
`;

const UserMessageContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-wrap: break-word;
  padding-bottom: 16px;
`;

const SuggestedResponseContent = styled.div`
  p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  ul,
  ol {
    margin: 4px 0;
    padding-left: 20px;
  }
  sup.cite-ref {
    color: ${p => p.theme.colors.purple400};
    font-size: var(--font-size-small);
    font-weight: 600;
  }
`;

const SuggestedResponseActions = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  padding-top: 8px;
`;

const SuggestedResponseCopy = styled.div`
  display: flex;
`;

const UseButton = styled(Button)`
  min-width: 64px;
`;

const RetryIconButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  flex-shrink: 0;
  font-size: var(--font-size-medium);
  height: 16px;
  line-height: 1;
  margin-top: 1px;
  padding: 0;
  width: 16px;

  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

const ThumbButton = styled.button`
  background: none;
  border: none;
  color: ${p => (p.isActive ? p.theme.colors.green400 : p.theme.colors.gray500)};
  cursor: pointer;
  flex-shrink: 0;
  height: 16px;
  line-height: 1;
  padding: 0;
  width: 16px;

  &:hover {
    color: ${p => (p.isActive ? p.theme.colors.green400 : p.theme.colors.gray700)};
  }
`;

const ThumbsUp = styled(ThumbsUpIcon)`
  fill: currentColor;
  height: 15px;
  width: auto;
`;

const ThumbsDown = styled(ThumbsDownIcon)`
  fill: currentColor;
  height: 15px;
  transform: scaleX(-1);
  width: auto;
`;

const ReviewReasonsContainer = styled.div`
  align-items: flex-start;
  background: ${p => p.theme.colors.yellow100};
  border-radius: 4px;
  display: flex;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 8px;
`;

const ReviewReasonsIcon = styled.i`
  color: ${p => p.theme.colors.yellow600};
  flex-shrink: 0;
  font-size: var(--font-size-reduced);
  margin-top: 2px;
`;

const ReviewReasonsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ReviewReasonsLabel = styled.span`
  color: ${p => p.theme.colors.yellow700};
  font-size: var(--font-size-reduced);
  font-weight: 600;
`;

const ReviewReason = styled.span`
  color: ${p => p.theme.colors.yellow600};
  font-size: var(--font-size-reduced);
  line-height: 1.3;
`;

const ProposedActionContainer = styled.div`
  background: ${p => p.theme.colors.yellow100};
  border: 1px solid ${p => p.theme.colors.yellow300};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding: 10px 12px;
`;

const ProposedActionHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`;

const ProposedActionIcon = styled.i`
  color: ${p => p.theme.colors.yellow600};
  flex-shrink: 0;
  font-size: 13px;
  margin-top: 3px;
`;

const ProposedActionTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ProposedActionLabel = styled.span`
  color: ${p => p.theme.colors.yellow700};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ProposedActionToolName = styled.span`
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const ProposedActionArgs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProposedActionExpander = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray700};
  cursor: pointer;
  font-size: 12px;
  padding: 0;

  &:hover {
    color: ${p => p.theme.colors.black};
    text-decoration: underline;
  }
`;

const ProposedActionArgsBlock = styled.pre`
  background: ${p => p.theme.colors.gray100};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
  max-height: 200px;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ProposedActionActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-top: 2px;
`;

const ProposedActionMutedState = styled.div`
  color: ${p => p.theme.colors.gray700};
  font-size: 12px;
  font-style: italic;
`;

const ProposedActionErrorBlock = styled.div`
  background: ${p => p.theme.colors.red100};
  border-radius: 4px;
  padding: 8px;
`;

const ProposedActionErrorHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
`;

const ProposedActionErrorIcon = styled.i`
  color: ${p => p.theme.colors.red400};
  font-size: 12px;
`;

const ProposedActionErrorText = styled.span`
  color: ${p => p.theme.colors.red400};
  flex: 1;
  font-size: 12px;
  font-weight: 600;
`;

const ProposedActionErrorExpander = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.red400};
  cursor: pointer;
  font-size: 11px;
  padding: 0;

  &:hover {
    color: ${p => p.theme.colors.red300};
    text-decoration: underline;
  }
`;

const ProposedActionErrorBody = styled.pre`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.red300};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
  margin: 8px 0 0 0;
  max-height: 240px;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CitationsDivider = styled.hr`
  border: none;
  border-top: 1px solid ${p => p.theme.colors.gray300};
  height: 0;
  margin: 12px 0 8px 0;
`;

const CitationsLabel = styled.span`
  color: ${p => p.theme.colors.gray800};
  font-size: var(--font-size-reduced);
  margin-bottom: 4px;
`;

const CitationsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: var(--font-size-reduced);
  gap: 4px 12px;
  line-height: 1.3;
`;

const CitationNumber = styled.span`
  font-weight: 600;
`;

const CitationLink = styled.a`
  color: ${p => p.theme.colors.green400};
  text-decoration: none;

  &:hover {
    color: ${p => p.theme.colors.green300};
  }
`;

const FeedbackModalOverlay = styled.div`
  .modalCard-content {
    max-width: 400px;
    padding: 20px;
    width: 400px;
  }
`;

const FeedbackModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeedbackModalTitle = styled.h3`
  color: ${p => p.theme.colors.black};
  font-size: ${p => p.theme.fontSize.large};
  font-weight: 600;
  margin: 0;
`;

const FeedbackReasonButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FeedbackReasonButton = styled.button`
  background: ${p => (p.isSelected ? p.theme.colors.green400 : p.theme.colors.gray100)};
  border: 1px solid ${p => (p.isSelected ? p.theme.colors.green400 : p.theme.colors.gray300)};
  border-radius: 16px;
  color: ${p => (p.isSelected ? '#fff' : p.theme.colors.gray800)};
  cursor: pointer;
  font-size: var(--font-size-base);
  padding: 6px 14px;
  transition: all 0.15s ease;

  &:hover {
    background: ${p => (p.isSelected ? p.theme.colors.green300 : p.theme.colors.gray200)};
    border-color: ${p => (p.isSelected ? p.theme.colors.green300 : p.theme.colors.gray400)};
  }
`;

const FeedbackCommentArea = styled.textarea`
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 6px;
  color: ${p => p.theme.colors.black};
  font-family: inherit;
  font-size: var(--font-size-base);
  padding: 8px 10px;
  resize: vertical;

  &:focus {
    border-color: ${p => p.theme.colors.green400};
    outline: none;
  }

  &::placeholder {
    color: ${p => p.theme.colors.gray500};
  }
`;

const FeedbackModalActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;
